/**
 * RequireAuthProvider — central host for the "Sign in to interact" CTA.
 *
 * Wraps the app so that any descendant component can ask "is the visitor
 * authenticated, and if not, prompt them" through `useRequireAuth()` without
 * each surface having to own its own dialog state.
 *
 * The provider intentionally has no opinion on _what_ the action is — it
 * just knows that the user is not authenticated and that we need to ask
 * them to sign in before we run the wrapped callback. The wrapped callback
 * is never invoked for a guest, so the API call never fires.
 *
 * Also listens for `apiClient.onAuthChange` "expired" events so that a
 * mid-session 401 on a write surfaces the same CTA instead of bubbling up
 * as an unhandled rejection.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiClient } from "@shared/lib/api/client";
import { SignUpToInteract } from "./SignUpToInteract";
import type {
  RequireAuthIntent,
  RequireAuthPromptOptions,
  RequireAuthPromptState,
} from "./types";

export interface RequireAuthContextValue {
  /**
   * Wrap an action so it only runs when the visitor is authenticated. For a
   * guest visitor the CTA is shown and the action is NOT executed — no
   * network call, no optimistic update.
   */
  requireAuth<R>(
    action: () => R | Promise<R>,
    options?: RequireAuthPromptOptions,
  ): Promise<R | undefined>;
  /** Imperative trigger when there is no action to wrap (e.g. nav CTA). */
  promptLogin(options?: RequireAuthPromptOptions): void;
  /** Programmatically close the dialog (used by the dialog itself). */
  closePrompt(): void;
  /** Current dialog state — exposed mainly for tests. */
  promptState: RequireAuthPromptState;
}

/**
 * Default pass-through context: `requireAuth` runs the action immediately
 * and the dialog is never opened. This makes the hook safe to call from
 * components in tests that don't wrap with `<RequireAuthProvider>`, while
 * the real app always mounts the provider at the root (see
 * `frontend/src/main.tsx`). Tests that specifically need to exercise the
 * guest-vs-authed branches must wrap with the provider.
 */
const PASS_THROUGH_CONTEXT: RequireAuthContextValue = {
  async requireAuth(action) {
    return await action();
  },
  promptLogin: () => {},
  closePrompt: () => {},
  promptState: { open: false },
};

const RequireAuthContext =
  createContext<RequireAuthContextValue>(PASS_THROUGH_CONTEXT);

export interface RequireAuthProviderProps {
  /** Whether the visitor currently has a valid session. */
  isAuthenticated: boolean;
  /** Children — typically the whole app subtree. */
  children: ReactNode;
}

const CLOSED_STATE: RequireAuthPromptState = { open: false };

export function RequireAuthProvider({
  isAuthenticated,
  children,
}: RequireAuthProviderProps) {
  const [promptState, setPromptState] =
    useState<RequireAuthPromptState>(CLOSED_STATE);

  const closePrompt = useCallback(() => {
    setPromptState((prev) => ({ ...prev, open: false }));
  }, []);

  const promptLogin = useCallback((options?: RequireAuthPromptOptions) => {
    setPromptState({
      open: true,
      intent: options?.intent ?? "generic",
      returnTo: options?.returnTo,
    });
  }, []);

  // When the visitor becomes authenticated mid-flow (e.g. logs in in
  // another tab) close the prompt so it doesn't linger.
  useEffect(() => {
    if (isAuthenticated) {
      setPromptState((prev) => (prev.open ? CLOSED_STATE : prev));
    }
  }, [isAuthenticated]);

  // Mid-session 401: the API client emits `expired` when the refresh
  // attempt fails. Surface the same CTA instead of leaving the user with
  // an opaque error. (Authenticated → unauthenticated transitions caused
  // by an explicit `logout` are also handled here but the page-level guard
  // will usually redirect first.)
  useEffect(() => {
    const unsubscribe = apiClient.onAuthChange((event) => {
      if (event.reason === "expired" && !event.accessToken) {
        promptLogin({ intent: "generic" });
      }
    });
    return () => {
      unsubscribe();
    };
  }, [promptLogin]);

  const requireAuth = useCallback(
    async <R,>(
      action: () => R | Promise<R>,
      options?: RequireAuthPromptOptions,
    ): Promise<R | undefined> => {
      if (!isAuthenticated) {
        promptLogin(options);
        return undefined;
      }
      return await action();
    },
    [isAuthenticated, promptLogin],
  );

  const value = useMemo<RequireAuthContextValue>(
    () => ({ requireAuth, promptLogin, closePrompt, promptState }),
    [requireAuth, promptLogin, closePrompt, promptState],
  );

  return (
    <RequireAuthContext.Provider value={value}>
      {children}
      <SignUpToInteract
        open={promptState.open}
        intent={promptState.intent ?? "generic"}
        returnTo={promptState.returnTo}
        onOpenChange={(open) => {
          if (!open) closePrompt();
        }}
      />
    </RequireAuthContext.Provider>
  );
}

export function useRequireAuthContext(): RequireAuthContextValue {
  // Falls back to the pass-through default when no provider is mounted —
  // see PASS_THROUGH_CONTEXT above for the rationale.
  return useContext(RequireAuthContext);
}

/**
 * `useRequireAuth()` — primary entry point for interaction surfaces.
 *
 * Returns a function that takes the action to run when authenticated. For a
 * guest visitor it opens the CTA dialog and the action is NOT executed.
 */
export function useRequireAuth() {
  const { requireAuth, promptLogin, closePrompt, promptState } =
    useRequireAuthContext();
  return useMemo(
    () => ({ requireAuth, promptLogin, closePrompt, promptState }),
    [requireAuth, promptLogin, closePrompt, promptState],
  );
}

export type { RequireAuthIntent, RequireAuthPromptOptions };
